import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, Trophy, Flame, ChevronRight, Zap, Sparkles } from "lucide-react";

import { Section } from "@/components/site/section";
import { NewsCard, FixtureCard, dateUtils } from "@/components/site/cards";

import { Img } from "@/components/site/img";
import { Crest } from "@/components/site/crest";
import { Flag, MatchFlags, getArgentinaMatchOpponent } from "@/components/site/flag";
import { PHOTOS, matchPoster, pickPhoto, resolveHomeHeroImage, FEATURED_EGYPT_SLUG } from "@/assets/photos";
import {
  newsByCategoryQuery,
  fixturesQuery,
  leagueTableQuery,
  pollsQuery,
  productsQuery,
} from "@/lib/site-queries";

export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    const heroImage = resolveHomeHeroImage((loaderData as { news?: Array<{ slug?: string | null; cover_image?: string | null }> } | undefined)?.news);
    return {
      meta: [
        { title: "Argentinian.Soccer — Argentina Football News" },
        {
          name: "description",
          content:
            "The home of Argentina football. National Team coverage, Primera División, transfers, polls and the diaspora abroad — updated daily on Argentinian.Soccer.",
        },
        { property: "og:title", content: "Argentinian.Soccer — Argentina Football News" },
        { property: "og:url", content: "https://argentinian.soccer/" },
        { property: "og:image", content: heroImage },
        { name: "twitter:image", content: heroImage },
      ],
      links: [
        { rel: "canonical", href: "https://argentinian.soccer/" },
        // Preload the TRUE LCP candidate — derived from the same helper the
        // component renders, so the preload and the painted <img> share a URL.
        { rel: "preload", as: "image", href: heroImage, fetchpriority: "high" },
      ],
    };
  },
  loader: async ({ context }) => {
    const [news] = await Promise.all([
      context.queryClient.ensureQueryData(newsByCategoryQuery(undefined, 12)),
      context.queryClient.ensureQueryData(fixturesQuery("senior", 4)),
      context.queryClient.ensureQueryData(leagueTableQuery()),
      context.queryClient.ensureQueryData(pollsQuery()),
      context.queryClient.ensureQueryData(productsQuery()),
    ]);
    return { news };
  },
  component: HomePage,
});

const NEWS_SECTIONS: { key: string; label: string }[] = [
  { key: "europe", label: "Europe" },
  { key: "primera", label: "Primera División" },
  { key: "rest_of_world", label: "Rest Of The World" },
  { key: "transfers", label: "Transfers" },
  { key: "national_team", label: "National Team" },
  { key: "articles", label: "Articles" },
];



function articleSlug(article: { slug?: string | null; id?: string | null }) {
  return article.slug || article.id || "story";
}

function HomePage() {
  const { data: latest } = useSuspenseQuery(newsByCategoryQuery(undefined, 12));
  const { data: fixtures } = useSuspenseQuery(fixturesQuery("senior", 4));
  const { data: table } = useSuspenseQuery(leagueTableQuery());
  const { data: polls } = useSuspenseQuery(pollsQuery());
  const { data: products } = useSuspenseQuery(productsQuery());

  const egyptStory = latest.find((a) => a.slug === FEATURED_EGYPT_SLUG);
  const hero = egyptStory ?? latest[0];
  const secondary = latest.filter((a) => a.id !== hero?.id).slice(0, 4);
  const rest = latest.filter((a) => a.id !== hero?.id && !secondary.some((s) => s.id === a.id)).slice(0, 6);
  
  const nextMatch = fixtures.find((f) => f.argentina_score === null) ?? fixtures[0];
  const featuredProducts = products.slice(0, 3);
  const featuredPoll = polls[0];
  const heroImage = hero?.slug === FEATURED_EGYPT_SLUG
    ? matchPoster("Egypt") ?? hero.cover_image ?? PHOTOS.messiCelebration
    : hero?.cover_image || PHOTOS.messiCelebration;
  const heroOpponent = hero
    ? getArgentinaMatchOpponent(`${hero.title} ${hero.excerpt ?? ""} ${articleSlug(hero)}`)
    : null;

  const carouselCards = [
    nextMatch && {
      key: "match",
      eyebrow: "Next Match",
      title: `Argentina vs ${nextMatch.opponent}`,
      sub: `${dateUtils.fmtDate(nextMatch.kickoff)} · ${dateUtils.fmtTime(nextMatch.kickoff)} UTC`,
      tag: nextMatch.competition ?? "International",
      image: PHOTOS.teamHuddle,
      to: "/national-team/fixtures" as const,
      icon: Zap,
    },
    {
      key: "shop",
      eyebrow: "Drop",
      title: "2026 Home Kit",
      sub: "Three stars edition · Limited run",
      tag: "Shop",
      image: PHOTOS.messiHighfive,
      to: "/shop" as const,
      icon: Sparkles,
    },
    {
      key: "wc",
      eyebrow: "World Cup",
      title: "Road to 2026",
      sub: "Group draws, fixtures and reaction",
      tag: "Feature",
      image: PHOTOS.wcAustralia,
      to: "/national-team/world-cup" as const,
      icon: Trophy,
    },
  ].filter(Boolean) as Array<{
    key: string; eyebrow: string; title: string; sub: string; tag: string; image: string;
    to: "/national-team/fixtures" | "/shop" | "/national-team/world-cup";
    icon: React.ComponentType<{ className?: string }>;
  }>;

  return (
    <>
      {/* HERO — editorial intro + cinematic featured */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-background to-muted text-navy">
        {/* Soft sky wash for depth without the heavy navy block */}
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(120%_80%_at_85%_-10%,color-mix(in_oklab,var(--color-sky)_55%,transparent),transparent_55%)]" />
        <div aria-hidden className="absolute inset-x-0 top-0 h-1 sky-bar" />

        <div className="relative mx-auto grid max-w-7xl gap-6 px-4 pt-6 pb-10 sm:pt-8 lg:grid-cols-[1.35fr_1fr] lg:gap-10 lg:px-8 lg:pt-14 lg:pb-16 xl:grid-cols-[1.45fr_1fr] xl:gap-12">

          {/* MAIN FEATURED IMAGE — cinematic on every breakpoint */}
          {hero && (
            <div className="flex flex-col">
              <Link
                to="/news/$slug"
                params={{ slug: articleSlug(hero) }}
                preload="intent"
                aria-label={`Read article: ${hero.title}`}
                className="group block overflow-hidden rounded-3xl ring-1 ring-border bg-card shadow-stadium transition-all duration-500 hover:ring-sky/60 active:scale-[0.995]"
              >
                <div className="aspect-[16/9] w-full overflow-hidden sm:aspect-[16/10] lg:aspect-[5/6] xl:aspect-[4/5]">
                  <Img
                    src={heroImage}
                    alt={hero.title}
                    priority
                    width={1920}
                    height={1280}
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
              </Link>

              {/* Mobile headline — compact, below image */}
              <div className="mt-4 lg:hidden">
                <Link
                  to="/news/$slug"
                  params={{ slug: articleSlug(hero) }}
                  preload="intent"
                  className="group block"
                  aria-label={`Read article: ${hero.title}`}
                >
                  <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-sky-deep">
                    {(hero.category ?? "Featured").replace(/_/g, " ")}
                  </p>
                  <MatchFlags opponent={heroOpponent} className="mt-2" />
                  <h1 className="mt-1 line-clamp-2 font-stadium text-[1.35rem] leading-[1.05] text-navy">
                    {hero.title}
                  </h1>
                </Link>
              </div>
            </div>
          )}

          {/* RIGHT COLUMN — desktop headline + latest stack + CTAs */}
          <div className="flex flex-col gap-5 lg:gap-6">
            <div className="hidden lg:block">
              <p className="font-condensed text-xs font-bold uppercase tracking-[0.35em] text-sky-deep">
                Argentinian.Soccer
              </p>
              <h2 className="mt-2 font-stadium text-3xl leading-[0.95] text-navy xl:text-[2.25rem]">
                Three stars on the badge.<br />
                <span className="text-primary">One editorial home.</span>
              </h2>
            </div>

            {/* Desktop headline block — sits beside the hero image */}
            {hero && (
              <div className="hidden lg:block rounded-r-2xl border-l-[6px] border-sky bg-white/60 pl-6 py-2 shadow-[0_1px_0_0_rgba(15,23,42,0.04)]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-2 py-1 font-condensed text-[0.7rem] font-bold uppercase tracking-[0.25em] text-primary-foreground shadow-lg shadow-primary/40">
                    <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                    Featured
                  </span>
                  <MatchFlags opponent={heroOpponent} className="rounded-sm bg-sky/20 px-2 py-1 ring-1 ring-sky/30" />
                </div>
                <p className="mt-3 font-condensed text-[0.7rem] font-bold uppercase tracking-[0.3em] text-sky-deep">
                  {(hero.category ?? "Feature").replace(/_/g, " ")}
                </p>
                <Link
                  to="/news/$slug"
                  params={{ slug: articleSlug(hero) }}
                  preload="intent"
                  className="group block"
                  aria-label={`Read article: ${hero.title}`}
                >
                  <h1 className="mt-2 font-stadium leading-[0.95] text-navy text-4xl xl:text-5xl 2xl:text-[3.5rem]">
                    {hero.title}
                  </h1>
                </Link>
              </div>
            )}


            <div className="flex items-end justify-between border-t border-border pt-4 lg:pt-5">
              <p className="font-stadium text-lg leading-none text-navy">Latest updates</p>
              <Link
                to="/news"
                className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-sky-deep border-b border-sky-deep/30 hover:border-sky-deep transition-colors"
              >
                View all
              </Link>
            </div>

            <ol className="flex flex-col">
              {secondary.map((a, i) => (
                <li key={a.id} className="border-b border-border last:border-b-0">
                  <Link
                    to="/news/$slug"
                    params={{ slug: articleSlug(a) }}
                    preload="intent"
                    aria-label={`Read article: ${a.title}`}
                    className="group flex items-start gap-4 py-3.5 transition-colors hover:bg-sky/5 -mx-2 px-2 rounded-lg"
                  >
                    <span className="font-stadium text-2xl leading-none text-sky-deep/40 tabular-nums w-7 shrink-0 pt-0.5 transition-colors group-hover:text-primary">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.22em] text-sky-deep">
                        {a.category.replace(/_/g, " ")}
                      </p>
                      <MatchFlags
                        opponent={getArgentinaMatchOpponent(`${a.title} ${a.excerpt ?? ""} ${articleSlug(a)}`)}
                        className="mt-1"
                      />
                      <h2 className="mt-1 line-clamp-2 font-display text-[0.98rem] leading-snug text-navy transition-colors group-hover:text-primary">
                        {a.title}
                      </h2>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 self-center text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              ))}
            </ol>

            <div className="flex gap-2 pt-1">
              <Link
                to="/news"
                className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-primary via-primary to-sky px-4 py-3 font-condensed text-xs font-bold uppercase tracking-widest text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:opacity-95 active:scale-95"
              >
                All news <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/national-team"
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-border bg-card px-4 py-3 font-condensed text-xs font-bold uppercase tracking-widest text-navy hover:border-sky hover:text-primary active:scale-95"
              >
                NT Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* EDITORIAL STATS STRIP — sets the broadsheet tone right under the hero */}
      <section aria-label="Albiceleste at a glance" className="border-b border-border bg-gradient-to-b from-background to-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-9">
          <div className="mb-4 flex items-center gap-3">
            <span aria-hidden className="h-4 w-1.5 rounded-full bg-primary" />
            <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.3em] text-sky-deep">
              By the numbers
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 border-t-2 border-navy pt-5 md:grid-cols-4">
            {[
              { v: "36", l: "Unbeaten run", c: "text-sky-deep" },
              { v: "15", l: "Copa titles", c: "text-navy" },
              { v: "3", l: "World Cups", c: "text-sky-deep" },
              { v: "#1", l: "FIFA ranking", c: "text-navy" },
            ].map((s, i) => (
              <div
                key={s.l}
                className={`group transition-transform duration-300 hover:-translate-y-0.5 ${i < 3 ? "md:border-r md:border-border md:pr-4" : ""}`}
              >
                <div className={`font-stadium text-4xl leading-none tabular-nums lg:text-5xl xl:text-6xl ${s.c}`}>{s.v}</div>
                <div className="mt-2 font-condensed text-[0.6rem] font-bold uppercase tracking-[0.28em] text-muted-foreground">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-10">
          <div className="mb-3 flex items-end justify-between lg:mb-5">
            <p className="eyebrow">Right now</p>
            <Link
              to="/news"
              className="hidden font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-sky-deep border-b border-sky-deep/30 hover:border-sky-deep transition-colors lg:inline-block"
            >
              See more
            </Link>
          </div>
          <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0">
            {carouselCards.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.key}
                  to={c.to}
                  className="group flex w-[82vw] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-sky/50 hover:shadow-stadium active:scale-[0.99] md:w-[48%] lg:w-auto"
                >
                  <div className="aspect-[16/9] w-full overflow-hidden lg:aspect-[16/10]">
                    <Img
                      src={c.image}
                      alt={c.title}
                      width={640}
                      height={400}
                      lowPriority
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-4 lg:p-5">
                    <div className="flex items-center gap-2 font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-sky-deep">
                      <Icon className="h-3 w-3" aria-hidden />
                      {c.eyebrow}
                      <span aria-hidden className="h-px flex-1 bg-border" />
                      <span className="text-navy/60">{c.tag}</span>
                    </div>
                    <h3 className="font-display text-lg leading-tight text-navy transition-colors group-hover:text-primary lg:text-xl">{c.title}</h3>
                    <p className="font-condensed text-xs uppercase tracking-wider text-muted-foreground">{c.sub}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>


      {/* NEXT MATCH STRIP */}
      {nextMatch && (
        <section className="border-b border-border bg-primary text-primary-foreground">
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-4 py-3 md:flex-row md:items-center md:py-4 lg:px-8">
            <Link
              to="/national-team/fixtures/$id"
              params={{ id: nextMatch.id! }}
              className="group flex items-center gap-3 rounded-md -mx-1 px-1 py-0.5 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label={`Argentina vs ${nextMatch.opponent} — match article`}
            >
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-sand" aria-hidden />
              <p className="font-condensed text-xs font-bold uppercase tracking-[0.3em]">Next Match</p>
              <div className="flex items-center gap-2 font-display text-lg md:text-xl">
                <Flag country="argentina" className="h-5 w-7" />
                Argentina
                <span className="text-primary-foreground">vs</span>
                {nextMatch.opponent}
                <Flag country={nextMatch.opponent} className="h-5 w-7" />
              </div>
              <ArrowRight aria-hidden className="h-4 w-4 opacity-70 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <p className="font-condensed text-xs font-semibold uppercase tracking-widest md:text-sm">
              {dateUtils.fmtDate(nextMatch.kickoff)} · {dateUtils.fmtTime(nextMatch.kickoff)} UTC · {nextMatch.competition}
            </p>
            <Link
              to="/national-team/fixtures"
              className="font-condensed text-xs font-bold uppercase tracking-widest underline-offset-4 hover:underline"
            >
              All fixtures →
            </Link>
          </div>
        </section>
      )}

      {/* NEWS */}
      <Section eyebrow="News" title="Latest Coverage" description="Jump straight into your section.">
        <div className="mb-6 -mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:flex-wrap lg:px-0">
          {NEWS_SECTIONS.map((s) => (
            <Link
              key={s.key}
              to="/news"
              hash={s.key}
              className="shrink-0 rounded-full border border-border bg-card px-3.5 py-1.5 font-condensed text-xs font-bold uppercase tracking-widest hover:border-primary hover:text-primary active:scale-95"
            >
              {s.label}
            </Link>
          ))}
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((a) => (
            <NewsCard key={a.id} article={a} />
          ))}
        </div>
      </Section>

      {/* NATIONAL TEAM */}
      <Section eyebrow="National Team" title="La Albiceleste">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="flex flex-col gap-5">
            <Link to="/national-team" className="group block overflow-hidden rounded-xl border border-border">
              <Img
                src={PHOTOS.teamLineup}
                alt="Argentina line-up"
                width={960}
                height={540}
                lowPriority
                className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </Link>
            <div>
              <p className="font-condensed text-xs font-bold uppercase tracking-[0.3em] text-sky-deep">National Team Hub</p>
              <h3 className="mt-2 font-display text-3xl text-navy">Squad · Fixtures · Stats</h3>
            </div>
          </div>
          <div className="space-y-3">
            {nextMatch && <FixtureCard fixture={nextMatch} />}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="eyebrow">Quick links</p>
              <ul className="mt-3 grid grid-cols-2 gap-1.5 font-condensed text-sm font-semibold uppercase tracking-wider">
                <li><Link to="/national-team/squad" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">Squad →</Link></li>
                <li><Link to="/national-team/fixtures" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">Fixtures →</Link></li>
                <li><Link to="/national-team/statistics" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">Statistics →</Link></li>
                <li><Link to="/national-team/world-cup" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">World Cup →</Link></li>
                <li><Link to="/national-team/women" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">Women →</Link></li>
                <li><Link to="/national-team/u20" className="block rounded-md px-3 py-2.5 hover:bg-secondary hover:text-primary">U20 →</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* PRIMERA */}
      <Section eyebrow="Primera División" title="Primera División Table">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-navy text-primary-foreground">
              <tr className="font-condensed text-xs uppercase tracking-widest">
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Club</th>
                <th className="px-3 py-3 text-center">P</th>
                <th className="px-3 py-3 text-center">GD</th>
                <th className="px-3 py-3 text-center">Pts</th>
              </tr>
            </thead>
            <tbody>
              {table.slice(0, 6).map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-3 font-display text-lg text-primary">{row.position}</td>
                  <td className="px-3 py-3">
                    <span className="flex items-center gap-2.5">
                      <Crest team={row.team} className="h-7 w-6" />
                      <span className="font-condensed font-semibold uppercase tracking-wider">{row.team}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">{row.played}</td>
                  <td className="px-3 py-3 text-center">{(row.goals_for ?? 0) - (row.goals_against ?? 0)}</td>
                  <td className="px-3 py-3 text-center font-display text-lg">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Link to="/primera-division" className="mt-4 inline-flex items-center gap-1 font-condensed text-sm font-bold uppercase tracking-widest text-primary">
          Full league table <ArrowRight className="h-4 w-4" />
        </Link>
      </Section>

      {/* POLLS */}
      {featuredPoll && (
        <Section eyebrow="Polls" title="This Week's Poll">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-2xl text-navy">{featuredPoll.question}</h3>
            <div className="mt-5 space-y-3">
              {featuredPoll.options.map((opt) => {
                const pct = featuredPoll.total ? Math.round((opt.votes / featuredPoll.total) * 100) : 0;
                return (
                  <div key={opt.id}>
                    <div className="flex justify-between font-condensed text-sm font-semibold uppercase tracking-wider">
                      <span>{opt.label}</span>
                      <span className="text-primary">{pct}%</span>
                    </div>
                    <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full bg-gradient-to-r from-sky to-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/forum/polls" className="mt-5 inline-flex items-center gap-1 font-condensed text-sm font-bold uppercase tracking-widest text-primary">
              All polls <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      )}

      {/* SHOP */}
      <Section eyebrow="Shop" title="Shop the Albiceleste">
        <div className="grid gap-5 md:grid-cols-3">
          {featuredProducts.map((p) => (
            <Link
              to="/shop"
              key={p.id}
              className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-stadium active:scale-[0.99]"
            >
              <div className="aspect-square overflow-hidden bg-secondary">
                <Img
                  src={pickPhoto(p.id)}
                  alt={p.name}
                  width={640}
                  height={640}
                  lowPriority
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2 p-5">
                <p className="eyebrow">{p.subcategory ?? p.category}</p>
                <h3 className="font-display text-xl text-navy">{p.name}</h3>
                <p className="line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
                <p className="mt-auto font-display text-2xl text-primary">${p.price}</p>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/shop" className="mt-4 inline-flex items-center gap-1 font-condensed text-sm font-bold uppercase tracking-widest text-primary">
          Visit the shop <ArrowRight className="h-4 w-4" />
        </Link>
      </Section>

      {/* CONTACT + ABOUT teaser */}
      <Section>
        <div className="grid gap-5 md:grid-cols-2">
          <Link to="/contact-us" className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:border-primary">
            <div className="absolute right-0 top-0 h-full w-1 bg-primary" />
            <p className="eyebrow">Contact Us</p>
            <h3 className="mt-2 font-display text-3xl text-navy group-hover:text-primary">Get in touch</h3>
            <p className="mt-2 text-sm text-muted-foreground">Editorial tips, partnerships, and job inquiries.</p>
          </Link>
          <Link to="/about-us" className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 hover:border-primary">
            <div className="absolute right-0 top-0 h-full w-1 bg-sky" />
            <p className="eyebrow">About Us</p>
            <h3 className="mt-2 font-display text-3xl text-navy group-hover:text-primary">Our story</h3>
            <p className="mt-2 text-sm text-muted-foreground">Independent fan media, built for Argentine football.</p>
          </Link>
        </div>
      </Section>
    </>
  );
}
